import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useFraudStats, useRecordings } from "../api/recordings";
import { errorMessage } from "../api/client";
import RecordingGrid from "../components/RecordingGrid";
import SortControl, { sortRecordings, type SortKey } from "../components/SortControl";
import UploaderFilter, { filterByUploader, uploaderOptions } from "../components/UploaderFilter";

const TABS = [
  { key: "potential_fraud", label: "Needs review", status: "potential_fraud" },
  { key: "fraud", label: "Confirmed", status: "fraud" },
] as const;

/** Fraud Detection: recordings the anti_fraud stage parked, waiting for a human verdict, plus
 * the ones already confirmed. Each card opens the detail page, where the decision is made. */
export default function FraudPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("t") ?? "potential_fraud";
  const active = TABS.find((t) => t.key === requested) ?? TABS[0];
  const setActive = (key: string) =>
    setSearchParams(key === TABS[0].key ? {} : { t: key }, { replace: true });
  const [uploader, setUploader] = useState("");
  const [sort, setSort] = useState<SortKey>("newest");

  const stats = useFraudStats();
  const { data, isLoading, error, isFetching } = useRecordings({ status: active.status, limit: 200 });
  const items = data?.items ?? [];
  const uploaders = useMemo(() => uploaderOptions(items), [items]);
  const visible = useMemo(
    () => sortRecordings(filterByUploader(items, uploader), sort),
    [items, uploader, sort],
  );
  const s = stats.data;

  return (
    <div className="recordings-page fraud-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Fraud Detection</h1>
          <p className="page-sub">
            Recordings the anti-fraud check parked before processing. Open one to confirm the
            fraud or send it back through the pipeline.
            {isFetching && !isLoading ? " · refreshing" : ""}
          </p>
        </div>
        <div className="page-head-actions">
          <SortControl sort={sort} onChange={setSort} />
        </div>
      </div>

      <div className="kpi-row fraud-kpis">
        <div className="kpi-tile">
          <span className="kpi-value">{s ? s.potential_count : "…"}</span>
          <span className="kpi-label">Needs review</span>
          <span className="kpi-sub">parked, no compute spent</span>
        </div>
        <div className="kpi-tile">
          <span className="kpi-value">{s ? s.confirmed_count : "…"}</span>
          <span className="kpi-label">Confirmed fraud</span>
          <span className="kpi-sub">terminal, never paid</span>
        </div>
        <div className="kpi-tile">
          <span className="kpi-value">{s ? s.llm_calls_30d : "…"}</span>
          <span className="kpi-label">Vision model calls</span>
          <span className="kpi-sub">last 30 days</span>
        </div>
        <div className="kpi-tile">
          <span className="kpi-value">{s ? `$${s.llm_cost_usd_30d.toFixed(2)}` : "…"}</span>
          <span className="kpi-label">Model spend</span>
          <span className="kpi-sub">OpenRouter, last 30 days</span>
        </div>
      </div>

      <div className="queue-toolbar">
        <div className="filter-pills">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={active.key === t.key ? "pill active" : "pill"}
              onClick={() => setActive(t.key)}
            >
              {t.label}
              <span className="pill-count">
                {s ? (t.key === "fraud" ? s.confirmed_count : s.potential_count) : "–"}
              </span>
            </button>
          ))}
        </div>
        <UploaderFilter options={uploaders} value={uploader} onChange={setUploader} />
      </div>

      {error && <p className="error">Failed to load recordings: {errorMessage(error)}</p>}

      <RecordingGrid
        items={visible}
        isLoading={isLoading}
        emptyText={active.key === "fraud" ? "No confirmed fraud." : "Nothing suspicious waiting."}
        emptyHint={
          active.key === "fraud"
            ? "Confirmed recordings show up here for the record."
            : "Recordings the anti-fraud check flags will land here before any heavy processing."
        }
      />
    </div>
  );
}
